import { CSSProperties, FC, ReactNode } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import cx from 'classnames'

import { Close } from 'common/icons'

export type ModalProps = {
  visible?: boolean

  onClose?: (e?: any) => void
  title?: ReactNode
  footer?: ReactNode
  children?: ReactNode
  closable?: boolean
  centered?: boolean
  hasMargin?: boolean
  isResponsive?: boolean
  size?: 'small' | 'large'
  width?: string | number
  style?: CSSProperties
  wrapClassName?: string
  className?: string
  zIndex?: number
  destroyOnClose?: boolean
  [key: string]: unknown
}

const ModalComponent: FC<ModalProps> = ({
  children,
  visible,
  onClose,
  title,
  footer,
  closable = true,
  hasMargin = true,
  isResponsive = false,
  size = 'small',
  width,
  style,
  className,
  zIndex,
  destroyOnClose: _destroyOnClose,
  wrapClassName: _wrapClassName,
  centered: _centered,
  ...rest
}) => {
  const isLarge = size === 'large'

  return (
    <Dialog.Root
      open={visible}
      modal={false}
      onOpenChange={(open) => {
        if (!open) {
          onClose?.()
        }
      }}
    >
      <Dialog.Portal>
        <div
          className="fixed inset-0 bg-gray-800/70 cursor-default"
          style={{ zIndex: zIndex ? zIndex - 1 : 999 }}
          onClick={() => onClose?.()}
        />
        <Dialog.Content
          className={cx(
            'updated-component',
            'fixed z-[1000] bg-white shadow-lg outline-none overflow-y-auto',
            {
              'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[calc(100vh-3rem)] w-[calc(100%-20rem)] min-w-[768px] rounded-lg':
                isLarge,
              'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[calc(100vh-3rem)] rounded':
                !isLarge,
              'w-auto': isResponsive,
              'min-w-[520px] w-auto max-w-[90vw]':
                !isLarge && !isResponsive && !width,
            },
            className
          )}
          style={{
            ...style,
            ...(width ? { width } : {}),
            ...(zIndex ? { zIndex: (zIndex as number) + 1 } : {}),
          }}
          aria-describedby={undefined}
          {...rest}
        >
          {title && (
            <Dialog.Title className="px-6 pt-6 text-lg font-semibold text-base-700">
              {title}
            </Dialog.Title>
          )}
          {!title && <Dialog.Title className="sr-only">Dialog</Dialog.Title>}

          {closable !== false && (
            <Dialog.Close
              className="absolute z-10 top-6 right-6 text-coolGray-400 focus:outline-none focus-visible:outline-black"
              onClick={onClose}
            >
              <Close isDefaultColor={false} />
            </Dialog.Close>
          )}

          <div className={cx({ 'p-6': hasMargin, 'p-0': !hasMargin })}>
            {children}
          </div>

          {footer != null && (
            <div className="flex justify-end px-6 pb-4">{footer}</div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export default ModalComponent
